import { clsx } from 'clsx';
import {
  type ComponentPropsWithoutRef,
  forwardRef,
  memo,
  type PropsWithChildren,
  useRef,
} from 'react';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { inlineHandleTrack } from './resize-separator/ResizeSeparator.sty.js';
import { ResizeSeparator } from './resize-separator/ResizeSeparator.js';
import {
  ContentSlot,
  extractSlotsProps,
  PanelSlot,
  ResizeHandleSlot,
} from './slots.js';
import * as styles from './SplitLayout.sty.js';
import { useSplitSizing } from './useSplitSizing.js';

/** shell component for referencial stability - must not use any context or props */
const PanelShell = memo(({ children }: PropsWithChildren) => {
  return children;
});

/** shell component for referencial stability - must not use any context or props */
const ContentShell = memo(({ children }: PropsWithChildren) => {
  return children;
});

/** shell component for referencial stability - must not use any context or props */
const ResizeHandleShell = memo(({ children }: PropsWithChildren) => {
  return children;
});

interface PanelColumnProps
  extends PropsWithChildren, ComponentPropsWithoutRef<'div'> {}

const PanelColumn = forwardRef<HTMLDivElement, PanelColumnProps>(
  (props, forwardedRef) => {
    const { children, className, style, ...rest } = props;

    return (
      <div
        {...rest}
        ref={forwardedRef}
        className={clsx(styles.column, className)}
        style={style}
      >
        {children}
      </div>
    );
  },
);

interface ContentColumnProps
  extends PropsWithChildren, ComponentPropsWithoutRef<'div'> {}

const ContentColumn = forwardRef<HTMLDivElement, ContentColumnProps>(
  (props, forwardedRef) => {
    const { children, className, style, ...rest } = props;

    return (
      <div
        {...rest}
        ref={forwardedRef}
        className={clsx(styles.column, className)}
        style={style}
      >
        {children}
      </div>
    );
  },
);

/** @internal */
export interface SplitLayoutProps
  extends
    PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {}

const SplitLayoutRoot = memo(
  forwardRef<HTMLDivElement, SplitLayoutProps>((props, forwardedRef) => {
    const { children, className: rootClassname, ...rootProps } = props;

    const [behavioralTrackingProps, restRootProps] =
      useBehavioralTrackingProps(rootProps);

    const slots = extractSlotsProps(children);

    const rootRef = useRef<HTMLDivElement>(null);
    const rootMergedRef = useMergeRefs([forwardedRef, rootRef]);

    const generatedPanelId = useId();
    const panelId = slots.panel.domProps.id ?? generatedPanelId;

    const panelRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const panelMergedRef = useMergeRefs([panelRef, slots.panel.ref]);
    const contentMergedRef = useMergeRefs([contentRef, slots.content?.ref]);

    const sizing = useSplitSizing({
      panel: {
        min: slots.panel.config.minWidth,
        max: slots.panel.config.maxWidth,
        default: slots.panel.config.defaultWidth,
        position: slots.panel.config.position,
        resizable: slots.panel.config.resizable,
        onResize: slots.panel.config.onResize,
        width: slots.panel.config.width,
      },
      content: {
        min: slots.content?.config.minWidth ?? 0,
      },
      refs: {
        root: rootRef,
        panel: panelRef,
        content: contentRef,
      },
    });

    const isDefaultPosition = slots.panel.config.position === 'start';

    const panelColumn = (
      <PanelColumn
        {...slots.panel.domProps}
        id={panelId}
        style={{ ...slots.panel.domProps.style, width: sizing.panel.widthCss }}
        ref={panelMergedRef}
      >
        <PanelShell>{slots.panel.children}</PanelShell>
      </PanelColumn>
    );

    const contentColumn = slots.content ? (
      <ContentColumn
        {...slots.content.domProps}
        style={{
          ...slots.content.domProps.style,
          width: sizing.content.widthCss,
          // content needs an explicit minWidth when it is the leading column
          // (end position) to prevent it from collapsing before the resize handle
          minWidth: isDefaultPosition
            ? undefined
            : slots.content.config.minWidth,
        }}
        ref={contentMergedRef}
      >
        <ContentShell>{slots.content.children}</ContentShell>
      </ContentColumn>
    ) : null;

    const resizeHandleDecorations = slots.resizeHandle?.children ? (
      <div className={styles.handleDecorations}>
        <ResizeHandleShell>{slots.resizeHandle.children}</ResizeHandleShell>
      </div>
    ) : null;

    const resizeHandle = slots.panel.config.resizable ? (
      <div
        {...slots.resizeHandle?.domProps}
        className={clsx(
          inlineHandleTrack,
          slots.resizeHandle?.domProps?.className,
        )}
        ref={slots.resizeHandle?.ref}
      >
        <ResizeSeparator
          {...sizing.dragActions}
          controls={panelId}
          value={sizing.panel.width}
          min={sizing.panel.minPx}
          max={sizing.panel.maxPx}
          valueText={`${sizing.panel.width}px`}
          aria-label={
            slots.resizeHandle?.domProps['aria-label'] ??
            slots.panel.config.label
          }
          aria-labelledby={slots.resizeHandle?.domProps['aria-labelledby']}
        />
        {resizeHandleDecorations}
      </div>
    ) : null;

    return (
      <div
        {...restRootProps}
        {...behavioralTrackingProps}
        className={clsx(styles.layout, rootClassname)}
        ref={rootMergedRef}
      >
        {isDefaultPosition ? panelColumn : contentColumn}
        {resizeHandle}
        {isDefaultPosition ? contentColumn : panelColumn}
      </div>
    );
  }),
);
SplitLayoutRoot.displayName = 'SplitLayout.Root';

/**
 * Internal: The accessibility approach to SplitLayout is inspired by the Window Splitter ARIA pattern.
 * While we dont follow it 100%, most aria attributes and intraction should adhere to this pattern.
 * https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/
 */

/** @internal */
export const SplitLayout = Object.assign(SplitLayoutRoot, {
  Panel: PanelSlot,
  Content: ContentSlot,
  ResizeHandle: ResizeHandleSlot,
});
